import kyWithSentry from './helper/kyWithSentry';

import { EntityTypes } from 'context/entityContext';

import getTrendUrl, { ITrendUrlArgs } from 'url-constructors/getTrendUrl';
import getAudienceTrendUrl, {
  IAudienceTrendUrlArgs,
} from 'url-constructors/Audience/getAudienceTrendURL';

export interface IAudienceTrendArgs {
  customerFilter?: string[];
  countryIds?: string[];
  entityType: EntityTypes;
  entityIds: string[];
  period?: string;
  compareMode?: string;
  ageGroupsMode?: 'grouped' | 'summed';
  gendersMode?: 'grouped' | 'summed';
  breakdown?: 'streams' | 'listeners';
}

const audienceTrendInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData: IAudienceTrendArgs
): Promise<Response> => {
  const {
    customerFilter,
    countryIds,
    entityType,
    entityIds,
    period,
    ageGroupsMode = 'summed',
    gendersMode = 'summed',
    breakdown,
    compareMode,
  } = interceptionData;

  const withAgeBreakdown = ageGroupsMode === 'grouped' && breakdown;

  const trendOptions = {
    entityIds,
    entityType,
    countryIds,
    period,
    customerFilter,
    salesType: 'streaming',
    compareMode,
  };

  const uniqueListenersOptions = {
    ...trendOptions,
    ageGroupsMode,
    gendersMode,
    isUnique: true,
  };

  const streamingTrendFetch = async (
    withAgeBreakdown: boolean,
    gender: 'grouped' | 'summed'
  ): Promise<any> => {
    const streamingTrendUrl = !withAgeBreakdown
      ? getTrendUrl(trendOptions as ITrendUrlArgs)
      : getAudienceTrendUrl({
          ...trendOptions,
          ageGroupsMode,
          gendersMode: gender,
        } as IAudienceTrendUrlArgs);
    return kyWithSentry.get(streamingTrendUrl).json();
  };

  const uniqueListenersTrendFetch = async (
    withAgeBreakdown: boolean,
    gender: 'grouped' | 'summed'
  ): Promise<any> => {
    const uniqueListenersUrl = getAudienceTrendUrl({
      ...uniqueListenersOptions,
      gendersMode: gender,
    } as IAudienceTrendUrlArgs);
    return kyWithSentry.get(uniqueListenersUrl).json();
  };

  try {
    let response;
    if (!withAgeBreakdown) {
      const promises: Promise<any>[] = [
        streamingTrendFetch(false, gendersMode),
      ];

      const [streamingTrendData] = await Promise.all(promises);

      response = [
        ...streamingTrendData.map((data: any) => ({
          breakdown: 'streams',
          ...data,
        })),
      ];
    } else {
      // Age Breakdown
      const fetch =
        breakdown === 'streams'
          ? streamingTrendFetch
          : uniqueListenersTrendFetch;

      const promises: Promise<any>[] = [
        fetch(true, gendersMode),
        fetch(true, 'summed'),
      ];

      const [groupedData, summedData] = await Promise.all(promises);

      response = groupedData.map((gd: any, index: number) => ({
        breakdown,
        countryId: gd.countryId,
        entity: gd.entity,
        demographicsData: [
          ...gd.demographicsData,
          ...summedData[index]?.demographicsData.map((data: any) => ({
            gender: 'all',
            ...data,
          })),
        ],
      }));
    }
    return new Response(JSON.stringify(response));
  } catch (error) {
    const errorInfo = await error.response.json();
    return new Response(
      JSON.stringify({
        reason: {
          message: errorInfo.message,
        },
      })
    );
  }
};

export default audienceTrendInterceptor;
